import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger, useDetectScroll } from '@saviynt/common';
import {
  AlertBanner,
  Box,
  Button,
  ButtonIcon,
  ButtonSelect,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  Link as DSLink,
  Menu,
  type MenuOption,
  ModalIsland,
  Switch,
  Typography,
} from '@saviynt/design-system';
import { format } from 'date-fns';

import {
  getUTCEpochFromLocalDate,
  SodListLoader,
} from '../../../../../shared/components';
import {
  ABORT_ERROR,
  CF_NEU_BLD,
  CF_NEU_MED,
  MIN_COMMENT_LENGTH,
  SEARCH_TEXT_LENGTH,
  VIOLATIONS_DETAILS_TABS,
} from '../../../../../shared/constants';
import {
  type NavigateToDetailsProps,
  useInvertedLocalize,
  useInViewObserver,
  useNavigateTo,
} from '../../../../../shared/hooks';
import { type ViolationsActionManagerState } from '../../../../../shared/types';
import { createDevRejectionHandler } from '../../../../../shared/utils';
import { detailsActions } from '../../../../Details';
import { useHandleMitigatingControl } from '../../../../Details/hooks';
import { useLazyGetRecommendedMitiatingControlListQuery } from '../../../data-layer/violationsTabApi';

import { SORT_ACTION_MAPPING } from './constants';
import { useMcSort } from './use-mclistsort';
import ViolationsMitigateListItem from './ViolationsMitigateListItem';

import './ViolationsMitigate.css';

const MAX_COUNT = 50;

export interface TrackerState {
  id: string;
  isChecked: boolean;
  startDate: Date | null | undefined;
  endDate: Date | null | undefined;
  comment: string;
  isCommentCritical?: boolean;
  isStartDateCritical?: boolean;
  isEndDateCritical?: boolean;
}

interface McListRowState {
  mitigatingControlId: number;
  mitigatingControl: string;
  ruleset: string;
  expiryDate: string;
  priority: string;
  status: string;
  comments: string | null;
  description: string;
  owner: string | null;
  businessProcess: string | number | null;
  controlType: string;
}

interface TrackedMcListRowState extends McListRowState {
  tracker: TrackerState;
}

interface ViolationsMitigateState {
  sodKey: string;
  isAddMc: boolean;
  isOpen: boolean;
  isLoading: boolean;
  riskId?: string;
  bulkCount: number;
  handleSumbit: (
    comment: string,
    {
      selectedControls,
    }: {
      selectedControls: {
        id: number;
        startDate: string;
        endDate: string;
        comments: string;
      }[];
    }
  ) => void;
  handleModalClose: () => void;
  title: string;
  isDisabled?: boolean;
  redirectProps?: NavigateToDetailsProps;
  globalDateFormat: string;
  isDetailsPage?: boolean;
  violationActionManager: ViolationsActionManagerState | null;
}

/**
 * ViolationsMitigate
 */
function ViolationsMitigate({
  sodKey,
  isAddMc,
  isOpen,
  isLoading,
  isDisabled,
  riskId,
  bulkCount,
  title,
  handleSumbit,
  handleModalClose,
  redirectProps,
  globalDateFormat,
  isDetailsPage,
  violationActionManager,
}: ViolationsMitigateState) {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const { navigateToDetails } = useNavigateTo();

  const sectionRef = useRef(null);
  const { isScrolled: sectionRefIsScrolled }: { isScrolled: boolean } =
    useDetectScroll(null, sectionRef);

  // API
  const [fetchRecommendedControlList, { isLoading: isRecommendedLoading }] =
    useLazyGetRecommendedMitiatingControlListQuery();
  const { loadMitigateRow, mitigatingControlRows } = useHandleMitigatingControl(
    { isCacheDisabled: true }
  );

  // Controls
  const [isCritical, setIsCritical] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isRecommended, setIsRecommended] = useState(false);

  // Offset, observer and lists
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isObserverInView, setIsObserverInView] = useState(true);
  const [isListError, setIsListError] = useState(false);
  const [listRows, setListRows] = useState([] as TrackedMcListRowState[]);

  // MC list controls
  const [isLoadingMc, setIsLoadingMc] = useState(
    Boolean(isAddMc && sodKey.length > 0)
  );
  const [isErrorMc, setIsErrorMc] = useState(false);
  const [isMitigateBanner, setIsMitigateBanner] = useState(true);

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setIsObserverInView(isInView);
  });

  // Constants
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        totalCount === null ||
          (currentOffset < totalCount && listRows.length < totalCount)
      ),
    [currentOffset, totalCount, listRows.length]
  );
  const isListZero = useMemo(
    () => Boolean(listRows.length === 0),
    [listRows.length]
  );
  const isZeroCount = useMemo(
    () => Boolean(isListZero && totalCount !== null && !isValidApiCall),
    [totalCount, isListZero, isValidApiCall]
  );
  const isSearchActive = useMemo(
    () => Boolean(searchValue.length >= SEARCH_TEXT_LENGTH),
    [searchValue]
  );

  // Mitiigating controls list
  useEffect(() => {
    if (sodKey && sodKey.length > 0 && isAddMc) {
      const requestData = {
        id: parseInt(sodKey, 10),
        violationId: sodKey,
      };

      setIsErrorMc(false);

      loadMitigateRow(requestData)
        .catch(() => setIsErrorMc(true))
        .finally(() => setIsLoadingMc(false));
    }
  }, [loadMitigateRow, isAddMc, sodKey]);

  // Sort
  const {
    fields,
    fieldName,
    actions,
    actionName,
    handleFieldClick,
    handleActionClick,
    activeField,
    activeAction,
  } = useMcSort();

  // Call through list API
  const addTracker = (listData: McListRowState[]): TrackedMcListRowState[] =>
    listData.map((listRow) => ({
      ...listRow,
      tracker: {
        id: String(listRow.mitigatingControlId),
        isChecked: false,
        startDate: null,
        endDate: null,
        comment: '',
        isCommentCritical: false,
        isStartDateCritical: false,
        isEndDateCritical: false,
      },
    }));

  const handleMitigatingControlList = useCallback(
    async (offset: number) => {
      abortRef.current?.('New Search');

      if (riskId === undefined) {
        throw Error('Risk ID is not defined');
      }

      const payload = {
        offset,
        riskId: Number(riskId),
        max: MAX_COUNT,
        columnToSort: activeField,
        order: SORT_ACTION_MAPPING[activeAction ?? 'ASC'],
        searchText: searchValue.trim(),
        recommended: isRecommended,
      };

      try {
        const controller = fetchRecommendedControlList(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch (error) {
        if ((error as { name?: string }).name !== ABORT_ERROR) {
          setIsListError(true);
          setIsObserverInView(false);

          throw error;
        }

        throw Error(ABORT_ERROR);
      }
    },
    [
      activeField,
      activeAction,
      searchValue,
      isRecommended,
      riskId,
      fetchRecommendedControlList,
    ]
  );

  // Infinite scroll effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isObserverInView || !isValidApiCall || isLoadingMc) {
        return;
      }

      Logger.log('Fetching', currentOffset, 'to', currentOffset + MAX_COUNT);

      handleMitigatingControlList(currentOffset)
        .then((response) => {
          setIsObserverInView(false);
          setCurrentOffset((prev) => prev + MAX_COUNT);
          setIsListError(false);

          const { totalRecords, data } = response as {
            totalRecords: number;
            data: McListRowState[] | null | undefined;
          };

          if (!data) {
            setTotalCount(0);

            return;
          }

          const excludedIds = mitigatingControlRows.map((c) => c.id);
          const newRows = addTracker(data).filter(
            (row) => !excludedIds.includes(row.mitigatingControlId)
          );

          setListRows((prev) => [...prev, ...newRows]);
          setTotalCount(totalRecords);
        })
        .catch(
          createDevRejectionHandler(
            'ViolationsMitigate: handleMitigatingControlList'
          )
        );
    }, 10);

    return () => clearTimeout(debounceTimer);
  }, [
    currentOffset,
    isLoadingMc,
    handleMitigatingControlList,
    mitigatingControlRows,
    isObserverInView,
    isValidApiCall,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isLoadingMc) {
        setTotalCount(null);
        setListRows([]);
        setIsListError(false);
        setCurrentOffset(0);
        setIsObserverInView(true);
      }
    }, 100);

    return () => clearTimeout(debounceTimer);
  }, [isLoadingMc, searchValue, isRecommended, activeField, activeAction]);

  // Utils
  const handleSelection = (id: string) => {
    setListRows((prev) =>
      prev.map((item) => ({
        ...item,
        tracker: {
          ...item.tracker,
          isChecked:
            item.tracker.id === id
              ? !item.tracker.isChecked
              : item.tracker.isChecked,
          startDate:
            item.tracker.id === id && item.tracker.isChecked
              ? null
              : item.tracker.startDate,
          endDate:
            item.tracker.id === id && item.tracker.isChecked
              ? null
              : item.tracker.endDate,
          comment:
            item.tracker.id === id && item.tracker.isChecked
              ? ''
              : item.tracker.comment,
          isStartDateCritical:
            item.tracker.id === id && item.tracker.isChecked
              ? false
              : item.tracker.isStartDateCritical,
          isEndDateCritical:
            item.tracker.id === id && item.tracker.isChecked
              ? false
              : item.tracker.isEndDateCritical,
          isCommentCritical:
            item.tracker.id === id && item.tracker.isChecked
              ? false
              : item.tracker.isCommentCritical,
        },
      }))
    );
  };

  const handleStartDate = (id: string, startDate: Date | null | undefined) => {
    setListRows((prev) =>
      prev.map((item) => {
        if (item.tracker.id === id) {
          return {
            ...item,
            tracker: {
              ...item.tracker,
              startDate,
              isStartDateCritical:
                startDate === null || startDate === undefined,
            },
          };
        }

        return item;
      })
    );
  };

  const handleEndDate = (id: string, endDate: Date | null | undefined) => {
    setListRows((prev) =>
      prev.map((item) => {
        if (item.tracker.id === id) {
          return {
            ...item,
            tracker: {
              ...item.tracker,
              endDate,
              isEndDateCritical: endDate === null || endDate === undefined,
            },
          };
        }

        return item;
      })
    );
  };

  const handleComment = (id: string, comment: string) => {
    setListRows((prev) =>
      prev.map((item) => {
        if (item.tracker.id === id) {
          return {
            ...item,
            tracker: {
              ...item.tracker,
              comment,
              isCommentCritical: comment.trim().length < MIN_COMMENT_LENGTH,
            },
          };
        }

        return item;
      })
    );
  };

  const handleModalSubmit = () => {
    const validatedList = listRows.map((item) => ({
      ...item,
      tracker: {
        ...item.tracker,
        isCommentCritical:
          item.tracker.isChecked &&
          item.tracker.comment.trim().length < MIN_COMMENT_LENGTH,
        isStartDateCritical:
          item.tracker.isChecked &&
          (item.tracker.startDate === null ||
            item.tracker.startDate === undefined),
        isEndDateCritical:
          item.tracker.isChecked &&
          (item.tracker.endDate === null || item.tracker.endDate === undefined),
      },
    }));
    const checkedCount = validatedList.filter((item) => item.tracker.isChecked);

    if (checkedCount.length === 0) {
      setIsCritical(true);

      return;
    }

    const isValidationSuccessful = validatedList.every(
      (item) =>
        !item.tracker.isChecked ||
        (!item.tracker.isCommentCritical &&
          !item.tracker.isStartDateCritical &&
          !item.tracker.isEndDateCritical)
    );

    if (!isValidationSuccessful) {
      setListRows(validatedList);

      return;
    }

    handleSumbit('', {
      selectedControls: checkedCount.map((item) => ({
        id: item.mitigatingControlId,
        startDate: format(
          getUTCEpochFromLocalDate(
            item.tracker.startDate ? item.tracker.startDate : new Date()
          ),
          'yyyy-MM-dd HH:mm:ss'
        ),
        endDate: format(
          getUTCEpochFromLocalDate(
            item.tracker.endDate ? item.tracker.endDate : new Date()
          ),
          'yyyy-MM-dd HH:mm:ss'
        ),
        comments: item.tracker.comment,
      })),
    });
  };

  const renderBody = () => {
    if (isZeroCount) {
      return (
        <Typography
          kind='h2'
          color={CF_NEU_BLD}
          className='Sod-MitigateAction-bodyContent--isNoData'>
          {l(
            isSearchActive
              ? 'No Records Match Your Search'
              : 'No Records Found!'
          )}
        </Typography>
      );
    }

    return (
      <>
        {listRows.map((item) => (
          <ViolationsMitigateListItem
            key={item.tracker.id}
            mitigatingControl={item.mitigatingControl}
            priority={item.priority}
            expiryDate={item.expiryDate}
            controlType={item.controlType}
            description={item.description}
            tracker={item.tracker}
            globalDateFormat={globalDateFormat}
            isRecommended={isRecommended}
            handleSelection={handleSelection}
            handleStartDate={handleStartDate}
            handleEndDate={handleEndDate}
            handleComment={handleComment}
          />
        ))}
        {/* Observer Element */}
        {!isErrorMc && !isListError ? listObserverElement : null}
        {/* Loading Skeleton */}
        {!isErrorMc && !isListError && isValidApiCall && (
          <SodListLoader count={3} />
        )}
        {/* Retry mechanism */}
        {isListError && (
          <Typography
            kind='h2'
            color={CF_NEU_BLD}
            className='Sod-MitigateAction-bodyContent--isNoData'>
            {l('No Records Found!')}
          </Typography>
        )}
      </>
    );
  };

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleModalClose}
      classNames={{
        bodyClassName: 'Sod-RemediateActionModal-modalWrapper',
      }}
      HeaderComp={
        <div className='Sod-RemediateActionModal-headerWrapper'>
          <Box tag='div' className='Sod-RemediateActionModal-headerBox'>
            <Box tag='div' className='Sod-RemediateActionModal-header'>
              <Box tag='div' className='Sod-RemediateActionModal-headerText'>
                <Typography kind='h1' color={CF_NEU_BLD}>
                  {bulkCount > 1
                    ? l(
                        'Mitigate {count} {count, plural, one {Violation} other {Violations}}',
                        { count: bulkCount }
                      )
                    : l('Mitigate')}
                </Typography>
                <Box
                  tag='div'
                  className='Sod-RemediateActionModal-headerSubtitle'>
                  {bulkCount === 0 && (
                    <>
                      <Typography kind='body2' color={CF_NEU_MED}>
                        {l(
                          'Apply one or more mitigating controls to violation'
                        )}
                      </Typography>
                      <DSLink
                        kind='button'
                        text={title}
                        color='neutral'
                        target='_self'
                        onClick={() => {
                          if (
                            !isDetailsPage &&
                            violationActionManager !== null &&
                            redirectProps
                          ) {
                            dispatch(
                              detailsActions.setViolationTabdetailsActionManager(
                                {
                                  ...violationActionManager,
                                  isOpen: false,
                                }
                              )
                            );

                            handleModalClose();
                            navigateToDetails(redirectProps);
                          }
                        }}
                        size='medium'
                      />
                    </>
                  )}
                  {bulkCount > 0 && (
                    <>
                      <Typography kind='body2' color={CF_NEU_MED}>
                        {l(
                          'Apply one or more mitigating controls to {count, plural, one {# violation} other {# violations}} associated with risk',
                          { count: bulkCount }
                        )}
                      </Typography>
                      <Typography kind='body2' color={CF_NEU_MED}>
                        <span className='Sod-ViolationsControls-activeKindBoldText'>
                          &nbsp;{title}
                        </span>
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <ButtonIcon
            size='large'
            kind='ghost'
            ariaLabel={l('close')}
            icon={<Icon kind='Close' size='medium' />}
            onClick={handleModalClose}
            className='Sod-RemediateActionModal-closeButton'
          />
        </div>
      }
      isCloseButtonVisible={!isDisabled}
      className='Sod-MitigateAction'
      contentBody={
        <Box className='Sod-MitigateAction-bodyWrapper'>
          {isAddMc &&
            mitigatingControlRows.length > 0 &&
            isMitigateBanner &&
            !isDetailsPage &&
            bulkCount < 1 && (
              <InlineMessage
                kind='background'
                text={`${mitigatingControlRows.length} Mitigating Control${
                  mitigatingControlRows.length > 1 ? 's are' : ' is'
                } applied and not shown in this list.`}
                colorTheme='info'
                size='medium'
                isVisible={isMitigateBanner}
                isDismissible
                onDismiss={() => {
                  setIsMitigateBanner(false);
                }}
                leftIcon={<Icon kind='AlertInfoFilled' />}
                className='Sod-MitigateAction-mitigatingControlBanner'
                linkComponent={
                  <DSLink
                    text={`View Applied Control${
                      mitigatingControlRows.length > 1 ? 's' : ''
                    }`}
                    kind='button'
                    onClick={() => {
                      if (redirectProps && violationActionManager !== null) {
                        dispatch(
                          detailsActions.setViolationTabdetailsActionManager({
                            ...violationActionManager,
                            isOpen: false,
                          })
                        );
                        handleModalClose();

                        navigateToDetails({
                          ...redirectProps,
                          activeTab: VIOLATIONS_DETAILS_TABS.TAB_MITIGATING,
                        });
                      }
                    }}
                    suffixIconKind='ArrowRight'
                    className='Sod-MitigateAction-mitigatingControlBannerLink'
                  />
                }
              />
            )}
          <AlertBanner
            colorTheme='Critical'
            title={l('Selection Required')}
            description={l('Please select at least one mitigating control.')}
            shouldAutoDismiss
            isVisible={isCritical}
            onCancel={() => setIsCritical(false)}
          />
          <Box tag='div' className='Sod-MitigateAction-bodyControls'>
            <InputField
              size='medium'
              name='basic'
              placeholder={l('Search')}
              value={searchValue}
              setValue={setSearchValue}
              isDisabled={Boolean(
                isDisabled ?? (isRecommendedLoading || isLoadingMc)
              )}
              prefixIcon={
                <Icon kind='Search' color='--color-foreground-neutral-subtle' />
              }
            />
            <Menu
              isOutsideClickEnabled
              isPortalEnabled
              classNames={{
                dropdownClassName:
                  'Sod-MitigateAction-bodyControls-sortCustomMenu',
                customMenuClassName:
                  'Sod-MitigateAction-bodyControls-sortCustomMenu',
              }}
              CustomMenu={
                <div className='Sod-MitigateAction-bodyControls-sortWrapper'>
                  <Menu
                    options={fields}
                    onChange={(selectedField: MenuOption) => {
                      handleFieldClick(selectedField.value);
                    }}
                    trigger={
                      <ButtonSelect
                        kind='rectangle'
                        label={fieldName}
                        size='small'
                        isDisabled={Boolean(
                          isDisabled ?? (isRecommendedLoading || isLoadingMc)
                        )}
                      />
                    }
                    size='medium'
                    label={fieldName}
                    classNames={{
                      dropdownClassName:
                        'Sod-MitigateAction-bodyControls-sortCustomMenu',
                      customMenuClassName:
                        'Sod-MitigateAction-bodyControls-sortCustomMenu',
                    }}
                    isPortalEnabled
                  />
                  <Menu
                    options={actions}
                    onChange={(selectedAction: MenuOption) => {
                      handleActionClick(selectedAction.value);
                    }}
                    trigger={
                      <ButtonSelect
                        kind='rectangle'
                        label={actionName}
                        size='small'
                        isDisabled={Boolean(
                          isDisabled ?? (isRecommendedLoading || isLoadingMc)
                        )}
                      />
                    }
                    size='medium'
                    label={actionName}
                    classNames={{
                      dropdownClassName:
                        'Sod-MitigateAction-bodyControls-sortCustomMenu',
                      customMenuClassName:
                        'Sod-MitigateAction-bodyControls-sortCustomMenu',
                    }}
                    isPortalEnabled
                  />
                </div>
              }
              trigger={
                <ButtonSelect
                  kind='rectangle'
                  label={l('Sort')}
                  ariaLabel={l('Sort')}
                  onClick={() => {}}
                  isOpen={false}
                  prefixIcon={<Icon kind='Sort' />}
                  isVisualCueEnabled={false}
                  isDisabled={Boolean(
                    isDisabled ?? (isRecommendedLoading || isLoadingMc)
                  )}
                />
              }
            />

            <Box
              tag='div'
              className='Sod-MitigateAction-bodyControls--isRecommended'>
              <Icon kind='StarFilled' />
              <Typography kind='body2' color={CF_NEU_BLD}>
                {l('Recommended Only')}
              </Typography>
              <Switch
                isChecked={isRecommended}
                isDisabled={Boolean(
                  isDisabled ?? (isRecommendedLoading || isLoadingMc)
                )}
                onChange={() => setIsRecommended(!isRecommended)}
                ariaLabel={l('Toggle recommended')}
              />
            </Box>
          </Box>
          <Box tag='div' className='Sod-MitigateAction-bodyContent'>
            {renderBody()}
          </Box>
        </Box>
      }
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          <Button
            type='button'
            kind='subtle'
            size='medium'
            isDisabled={isDisabled ?? isLoading}
            onClick={handleModalClose}>
            {l('Cancel')}
          </Button>
          <Button
            type='button'
            kind='outlined'
            size='medium'
            onClick={handleModalSubmit}
            isDisabled={(isDisabled && !isLoading) || listRows.length === 0}
            isLoading={isLoading}
            loaderText={l('Applying')}>
            {l('Apply')}
          </Button>
        </FooterActionBar>
      }
    />
  );
}

ViolationsMitigate.defaultProps = {
  isDisabled: false,
  riskId: undefined,
  redirectProps: null,
  isDetailsPage: false,
};

export default memo(ViolationsMitigate);
